import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UserService } from '../../services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { OrganizationService } from '../../services/organization.service';
import { ProcessRolesService } from '../../services/config/process-roles.service';
import { SurveyService } from '../../services/surveys.service';
import { ToastService } from '../../services/general/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-single-team-member',
  templateUrl: './single-team-member.component.html',
  styleUrls: ['./single-team-member.component.scss'],
  standalone: false
})
export class SingleTeamMemberComponent implements OnInit {
  @Input() team_member: any;
  @Input() removable = true;
  @Input() context: string = null;
  @Input() surveyId = null;
  @Input() process_role_field = false;
  @Input() addButton = false;
  @Input() teamMemberCustomFields: any[] = [];
  @Input() mode: 'add' | 'edit' = 'add';
  @Output() removeTeamMemberEvent = new EventEmitter<number>();
  @Output() addedTeamMember = new EventEmitter<number>();
  @Output() onSave = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  loading = false;
  timerEmailUpdate;
  teamMember: any = {};
  suggestedTeamMemberUsers = [];
  professionalRoles = [];
  process_roles = [];
  constructor(
    public userService: UserService,
    public dialog: MatDialog,
    public organizationService: OrganizationService,
    public processRolesService: ProcessRolesService,
    public surveyService: SurveyService,
    public toast: ToastService,
    public translateService: TranslateService,
  ) {
  }

  ngOnInit(): void {
    if (!this.team_member) this.team_member = {};
    if (this.team_member.role === undefined || this.team_member.role === null) {
      this.team_member.role = '';
    }
    if (this.team_member.is_respondent === undefined || this.team_member.is_respondent === null) {
      this.team_member.is_respondent = true;
    }
    this.initializeCustoms(this.team_member);
    this.initializeCustoms(this.teamMember);
    this.init();
  }

  initializeCustoms(member: any) {
    if (!member) return;

    // Robust check: if customs is null, undefined, or an empty array [], reset to {}
    if (!member.customs || (Array.isArray(member.customs) && member.customs.length === 0)) {
      member.customs = {};
    } else if (Array.isArray(member.customs)) {
      // If it's an array with data (unexpected but possible), convert to object
      const obj = {};
      member.customs.forEach((val, idx) => obj[idx] = val);
      member.customs = obj;
    }

    if (!member.customs.team_member_custom_fields || Array.isArray(member.customs.team_member_custom_fields)) {
      member.customs.team_member_custom_fields = {};
    }

    const nested = member.customs.team_member_custom_fields;

    // 1. Migrate flat numeric keys to nested structure
    Object.keys(member.customs).forEach(key => {
      if (key !== 'team_member_custom_fields' && !isNaN(Number(key))) {
        nested[key] = member.customs[key];
        delete member.customs[key];
      }
    });

    // Ensure ID-based mapping for stability
    if (this.teamMemberCustomFields && this.teamMemberCustomFields.length > 0) {
      this.teamMemberCustomFields.forEach(field => {
        // Migration: If name exists but ID doesn't, move it (handle old data)
        if (nested[field.name] !== undefined && nested[field.id] === undefined) {
          nested[field.id] = nested[field.name];
          delete nested[field.name];
        }
      });
    }
  }

  init() {
    this.setTeamMemberByContext();
    this.professionalRoles =
      this.userService.getAllProfessionalRolesForCurrentOrganization();
    this.process_roles = this.processRolesService.getAllProcessRoles();
  }

  removeTeamMember() {
    this.removeTeamMemberEvent.emit();
  }

  onClickSearchUser(teamMember: any) {
    let email = '';
    if (teamMember?.email !== null && teamMember.email.length > 0) {
      email = teamMember?.email;
    }
    this.organizationService.getOrganizationUsersByEmail(
      email,
      this.userService.currentOrganization.id,
      (result) => {
        this.suggestedTeamMemberUsers = result.map(result => result.user);
      },
    );
  }

  setUserInfoByEmail(event, team_member) {
    const userFinded = this.suggestedTeamMemberUsers.find((user) => event.option.value.email === user.email);
    let first_name = '';
    let last_name = '';
    if (userFinded?.details?.first_name && userFinded?.details.last_name) {
      first_name = userFinded?.details.first_name;
      last_name = userFinded?.details.last_name;
    } else {
      first_name = userFinded?.name;
      last_name = userFinded?.last_name || '';
    }
    team_member.name = first_name || '';
    team_member.last_name = last_name || '';
    team_member.email = userFinded.email;

    this.suggestedTeamMemberUsers = [];
  }

  displayFn(option: any): string {
    return option.email || '';
  }

  onInputSearchUserChange(value: any, team_member) {
    team_member.email = value;
    if (this.timerEmailUpdate == null) {

      this.timerEmailUpdate = setTimeout(() => {
        this.timerEmailUpdate = null;
        if (value == '') {
          this.suggestedTeamMemberUsers = [];
        } else {
          this.organizationService.getOrganizationUsersByEmail(
            value,
            this.userService.currentOrganization.id,
            (result) => {
              this.suggestedTeamMemberUsers = result.map(result => result.user);
            },
          );
        }
      }, 800);
    }
  }

  getOrganizationChart(id) {
    return this.organizationService.organizationCharts.find((org_chart) => {
      return id === org_chart.id;
    });
  }

  addSurveyTeamMember(form) {

    if (!form.form.valid)
      return;

    this.loading = true;
    this.surveyService.addNewSurveyTeamMember(this.surveyId, this.teamMember, (result) => {
      this.loading = false;
      this.addedTeamMember.emit(result);
      this.toast.success(
        this.translateService.instant('generic.evaluation-session'),
        this.translateService.instant(
          'components.decision-table.new-decision-table.compile-proposal.team-member-added-successfully'
        )
      );
    }, () => {
      this.loading = false;
    });
  }

  save() {
    this.onSave.emit(this.team_member);
  }

  cancel() {
    this.onCancel.emit();
  }

  setTeamMemberByContext() {
    if (this.context === 'survey-team') {
      this.teamMember = {
        name: '',
        last_name: '',
        email: '',
        role: '',
        is_respondent: true,
        customs: {
          team_member_custom_fields: {}
        }
      };
    }
  }

  onRoleChange(newRole: string, memberObj: any) {
    if (!newRole) {
      memberObj.is_respondent = true;
    }
  }

  getCustomFieldIcon(type: string): string {
    switch (type) {
      case 'number': return 'fa-hashtag';
      case 'date': return 'fa-calendar-days';
      case 'boolean': return 'fa-square-check';
      default: return 'fa-font';
    }
  }

}
